import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { StatusBadge, ReasonPrompt } from "./shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import api, { formatEUR } from "../../lib/api";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

const TABS = ["pending", "approved", "rejected"];

export default function AdminReview() {
  const [tab, setTab] = useState("pending");
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/review?status=${tab}`);
      setFlags(r.data.flags || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const decide = async (reason) => {
    try {
      await api.post(`/admin/review/${prompt.flag.id}/${prompt.action}`, { reason });
      toast.success(`Flag ${prompt.action}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <AdminLayout title="Manual Review" subtitle="Auto-flagged orders and customers awaiting decision">
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-white border border-border rounded-xl p-1" data-testid="review-tabs">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white capitalize" data-testid={`tab-${t}`}>
              {t}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={tab}>
          <div className="xch-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="review-table">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <Th>Type</Th><Th>Customer</Th><Th>Reason</Th><Th right>Context</Th><Th>Flagged</Th><Th>Status</Th><Th />
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm">Loading…</td></tr>}
                  {!loading && flags.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm">Nothing in this queue.</td></tr>}
                  {flags.map((f) => (
                    <tr key={f.id} className="border-b border-border" data-testid={`review-row-${f.id}`}>
                      <td className="px-4 py-3"><span className="text-xs uppercase font-semibold text-muted-foreground">{f.entity_type}</span></td>
                      <td className="px-4 py-3">{f.customer_email || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-foreground">{f.reason.replace(/_/g, " ")}</td>
                      <td className="px-4 py-3 text-right text-xs tabular-nums">
                        {f.entity_type === "order" && f.entity ? `${formatEUR(f.entity.fiat_amount)} · ${f.entity.asset}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">{new Date(f.created_at).toLocaleString("en-IE")}</td>
                      <td className="px-4 py-3"><StatusBadge status={f.status} /></td>
                      <td className="px-4 py-3">
                        {f.status === "pending" && (
                          <div className="flex gap-1">
                            <Button size="sm" onClick={() => setPrompt({ action: "approve", flag: f })} className="bg-primary hover:bg-primary/90 text-white rounded-lg h-7 px-2" data-testid={`review-approve-${f.id}`}>
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setPrompt({ action: "reject", flag: f, danger: true })} className="rounded-lg h-7 px-2 text-destructive border-destructive/30 hover:bg-destructive/5" data-testid={`review-reject-${f.id}`}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                        {f.status !== "pending" && f.resolution_reason && (
                          <div className="text-xs text-muted-foreground italic max-w-[200px] truncate">{f.resolution_reason}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      <ReasonPrompt
        open={!!prompt} onOpenChange={() => setPrompt(null)}
        title={prompt?.action === "approve" ? "Approve flag" : "Reject flag"}
        description={prompt?.action === "reject"
          ? "Rejecting will cancel the order or freeze the customer."
          : "Approving clears the flag and lets the order continue."}
        onConfirm={decide}
        confirmLabel={prompt?.action === "approve" ? "Approve" : "Reject"}
        danger={prompt?.danger}
      />
    </AdminLayout>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${right ? "text-right" : "text-left"}`}>{children}</th>
);
